import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { payRenewal } from "../../../api/services/policyService";
import { usePaymentMethods } from "../../../hooks/usePaymentMethods";
import {
  chargeAmountForRenewalTerm,
  payableRenewalLateFee,
  payableRenewalCredit,
  renewalAmountDue,
} from "@libs/quote";
import { formatCurrency } from "../../../utils/formatCurrency";
import { useRenewal } from "../RenewalContext";
import { isRenewalTestMode } from "../renewalTestMode";

/**
 * "Renew Now" — pay the renewal immediately instead of waiting for the
 * automatic charge.
 *
 * Shown on the RENEW flow's confirmation screen, and only when the landlord
 * has already CONFIRMED the next term: an unconfirmed term isn't payable yet,
 * and move-out / not-sure tenants shouldn't be nudged to pay at all.
 *
 * It lives after submission rather than on the payment step so the
 * questionnaire is already saved before any money moves — a tenant who pays
 * and then abandons Review would otherwise leave a charged renewal with no
 * recorded response. Submit and charge stay separate actions, so a failed
 * charge is retryable without redoing the flow.
 *
 * The charge itself is the same `payRenewal` the standalone /policy-renewal
 * page calls, so promotion, emails and transaction records all happen exactly
 * as they do there — no second definition of what renewing means.
 */
const RenewNowCard: React.FC = () => {
  const { t } = useTranslation();
  const { policy, nextTerm, termUuid, draft } = useRenewal();
  const { methods } = usePaymentMethods();

  const [mandateAccepted, setMandateAccepted] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paid, setPaid] = useState<"succeeded" | "processing" | null>(null);

  const eligible =
    nextTerm?.status === "confirmed" && !isRenewalTestMode(termUuid);

  // Falls back to the tenant's default: they may have reached the
  // confirmation screen without touching the picker.
  const methodId =
    draft.paymentMethodId ??
    methods.find((m) => m.isDefault)?.id ??
    methods[0]?.id ??
    null;
  const selectedMethod = methods.find((m) => m.id === methodId) ?? null;
  const isBankSelected =
    selectedMethod !== null && selectedMethod.type !== "card";

  // Month-to-month quotes are the 12-month figure billed in 3-month slices,
  // so the ACH disclosure must state what's actually debited. A term that has
  // already started also carries the flat late fee — same helper the server
  // uses, so the button and the charge agree.
  //
  // An admin credit comes off after the late fee, exactly as the server
  // applies it, so the button, the ACH disclosure and the charge all agree.
  const baseAmount =
    chargeAmountForRenewalTerm(nextTerm?.total, nextTerm?.renewalTerm) ?? 0;
  const lateFee = payableRenewalLateFee(nextTerm);
  const {
    gross: grossAmount,
    credit,
    due: renewalAmount,
  } = renewalAmountDue(baseAmount, lateFee, payableRenewalCredit(nextTerm));
  const fullyCredited = credit > 0 && renewalAmount === 0;

  const handleRenewNow = async () => {
    if (!policy || paying) return;
    // A fully-credited renewal takes no money, so it needs neither a
    // payment method nor an ACH mandate — the server skips the charge
    // entirely. Requiring either would block a tenant who owes nothing.
    if (!fullyCredited) {
      if (!methodId) return;
      if (isBankSelected && !mandateAccepted) return;
    }
    setPaying(true);
    setError(null);
    try {
      const res = await payRenewal(
        policy.uuid,
        methodId as number,
        isBankSelected && !fullyCredited ? true : undefined,
      );
      if (res.data.status === "failed") {
        setError(
          res.data.error ??
            "We couldn't process that payment. Please try another method.",
        );
        return;
      }
      // `processing` is the ACH case — in flight, promoted by the webhook
      // once it clears. Still a success from the tenant's point of view.
      setPaid(res.data.status === "succeeded" ? "succeeded" : "processing");
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          "We couldn't process that payment. Please try again.",
      );
    } finally {
      setPaying(false);
    }
  };

  if (!eligible) return null;

  if (paid !== null) {
    return (
      <div className="mx-auto mt-6 max-w-md rounded-xl border-2 border-success/30 bg-white p-5 text-left">
        <p className="mb-1 text-sm font-semibold text-success">
          {paid === "succeeded"
            ? "Your renewal is paid ✓"
            : "Your payment is processing"}
        </p>
        <p className="mb-0 text-sm text-gray-700">
          {paid === "succeeded"
            ? "Your coverage has been renewed — a confirmation email is on its way."
            : "Bank payments take a few business days to clear. We'll email you once it's complete."}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-6 max-w-md rounded-xl border-2 border-success/30 bg-white p-5 text-left">
      <p className="mb-1 text-sm font-semibold text-success">
        Want to renew today?
      </p>
      {lateFee > 0 && (
        <p className="mb-2 text-sm font-medium text-danger">
          Includes a {formatCurrency(lateFee)} late fee — this renewal was due{" "}
          {nextTerm?.startsOn ?? "earlier"}.
        </p>
      )}
      {credit > 0 && (
        <div className="mb-3 rounded-lg bg-success/10 p-3 text-sm">
          <div className="flex justify-between text-gray-700">
            <span>Renewal</span>
            <span>{formatCurrency(grossAmount)}</span>
          </div>
          <div className="flex justify-between font-medium text-success">
            <span>Credit applied</span>
            <span>−{formatCurrency(credit)}</span>
          </div>
          <div className="mt-2 flex justify-between border-t border-success/20 pt-2 font-semibold text-gray-900">
            <span>{fullyCredited ? "Nothing to pay" : "You pay"}</span>
            <span>{formatCurrency(renewalAmount)}</span>
          </div>
        </div>
      )}
      <p className="mb-3 text-sm text-gray-700">
        {fullyCredited ? (
          <>
            A credit covers this renewal in full, so there is nothing to pay.
            Confirm below and your coverage continues.
          </>
        ) : (
          <>
            Your property manager has already confirmed this renewal, so you
            can pay now instead of waiting for the automatic charge
            {selectedMethod
              ? ` to your ${selectedMethod.brand ?? selectedMethod.type} ••••${selectedMethod.last4 ?? ""}`
              : ""}
            .
          </>
        )}
      </p>

      {isBankSelected && !fullyCredited && (
        <label className="mb-3 flex cursor-pointer items-start gap-3 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 cursor-pointer"
            checked={mandateAccepted}
            onChange={(e) => setMandateAccepted(e.target.checked)}
            disabled={paying}
          />
          <span>
            {t("policyRenewal.achAuthorization", {
              amount: formatCurrency(renewalAmount),
            })}
          </span>
        </label>
      )}

      {error && (
        <p className="mb-3 text-sm font-medium text-danger">{error}</p>
      )}

      <button
        type="button"
        onClick={handleRenewNow}
        disabled={
          paying ||
          (!fullyCredited &&
            (!methodId || (isBankSelected && !mandateAccepted)))
        }
        className="cursor-pointer rounded-lg bg-success px-5 py-2.5 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-300"
      >
        {paying
          ? "Processing…"
          : fullyCredited
            ? "Renew Now — no payment due"
            : `Renew Now — ${formatCurrency(renewalAmount)}`}
      </button>
    </div>
  );
};

export default RenewNowCard;
